import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, MagnifyingGlass, MapPin, Phone, EnvelopeSimple, Globe, ShieldCheck, User, HardHat, Heart, FolderOpen, Plus, Trash, X } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RADIUS_OPTIONS = [10, 25, 50, 100, 250, 500];

const ContractorCard = ({ gc, onExpand, isSaved, onToggleSave, onDragStart }) => {
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    if (gc.logo_file_id) {
      axios.get(`${API}/files/${gc.logo_file_id}/download`, { responseType: 'blob' })
        .then(res => setLogoUrl(URL.createObjectURL(res.data))).catch(() => {});
    }
  }, [gc.logo_file_id]);

  return (
    <div data-testid={`gc-card-${gc.id.slice(0,8)}`} className="hb-card overflow-hidden flex flex-col h-full relative"
      draggable onDragStart={(e) => { e.dataTransfer.setData('gc-id', gc.id); e.dataTransfer.effectAllowed = 'move'; if (onDragStart) onDragStart(gc.id); }}
      style={{ cursor: 'grab' }}>
      <button data-testid={`gc-save-${gc.id.slice(0,8)}`} onClick={(e) => { e.stopPropagation(); onToggleSave(gc.id); }}
        className="absolute top-3 right-3 p-1.5 rounded-lg z-10 transition-all"
        style={{ background: isSaved ? 'rgba(239,68,68,0.12)' : 'rgba(201,206,211,0.06)', border: isSaved ? '1px solid rgba(239,68,68,0.25)' : '1px solid transparent' }}
        title={isSaved ? 'Remove from saved' : 'Save contractor'}>
        <Heart size={18} weight={isSaved ? 'fill' : 'regular'} style={{ color: isSaved ? '#ef4444' : 'var(--text-muted)' }} />
      </button>
      <div className="p-5 flex flex-col items-center text-center flex-1">
        <div className="w-20 h-20 rounded-2xl overflow-hidden mb-4 flex items-center justify-center shrink-0"
          style={{ background: 'rgba(201,206,211,0.06)', border: '1px solid var(--border-subtle)' }}>
          {logoUrl ? <img src={logoUrl} alt={gc.company || gc.name} className="w-full h-full object-cover" />
            : <HardHat size={36} weight="duotone" style={{ color: 'var(--gold)', opacity: 0.5 }} />}
        </div>
        <div className="flex items-center gap-2 justify-center flex-wrap mb-1">
          <h3 className="text-sm font-bold font-heading" style={{ color: 'var(--text-primary)' }}>{gc.company || gc.name}</h3>
          {gc.verified === 'approved' && <ShieldCheck size={14} weight="fill" style={{ color: '#4ade80' }} />}
        </div>
        {gc.name && gc.company && <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{gc.name}</p>}
        {gc.location && <span className="flex items-center gap-1 text-xs mb-2" style={{ color: 'var(--text-secondary)' }}><MapPin size={11} weight="fill" style={{ color: 'var(--gold)' }} /> {gc.location}</span>}
        {gc.distance_miles != null && <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full mb-3 inline-block" style={{ background: 'rgba(201,206,211,0.08)', color: 'var(--gold)', border: '1px solid rgba(201,206,211,0.15)' }}>{gc.distance_miles} mi away</span>}
        <div className="flex flex-wrap gap-1.5 justify-center">
          {gc.phone && <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(201,206,211,0.06)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}><Phone size={10} /> {gc.phone}</span>}
          {gc.website && <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(201,206,211,0.06)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}><Globe size={10} /> Website</span>}
          {gc.bbb_number && <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.06)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.15)' }}><ShieldCheck size={10} /> BBB</span>}
        </div>
      </div>
      <button data-testid={`gc-view-${gc.id.slice(0,8)}`} onClick={() => onExpand(gc)} className="w-full px-5 py-3 text-xs font-semibold transition-all"
        style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--gold)', background: 'rgba(201,206,211,0.03)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(201,206,211,0.03)'; }}>
        View Profile
      </button>
    </div>
  );
};

const ProfileModal = ({ gc, onClose, isSaved, onToggleSave }) => {
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => { if (gc?.logo_file_id) { axios.get(`${API}/files/${gc.logo_file_id}/download`, { responseType: 'blob' }).then(res => setLogoUrl(URL.createObjectURL(res.data))).catch(() => {}); } }, [gc?.logo_file_id]);
  if (!gc) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}>
      <div data-testid="gc-profile-modal" className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', boxShadow: '0 25px 80px rgba(0,0,0,0.5)' }}>
        <div className="p-6 flex items-center gap-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0" style={{ background: 'rgba(201,206,211,0.06)', border: '1px solid var(--border-subtle)' }}>
            {logoUrl ? <img src={logoUrl} alt={gc.company} className="w-full h-full object-cover" /> : <HardHat size={32} weight="duotone" style={{ color: 'var(--gold)', opacity: 0.5 }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold font-heading" style={{ color: 'var(--gold-light)' }}>{gc.company || gc.name}</h2>
              {gc.verified === 'approved' && <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.1)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' }}><ShieldCheck size={10} weight="fill" /> Verified</span>}
            </div>
            {gc.location && <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><MapPin size={12} /> {gc.location}</p>}
          </div>
          <button onClick={() => onToggleSave(gc.id)} className="p-2 rounded-lg transition-all" style={{ background: isSaved ? 'rgba(239,68,68,0.12)' : 'rgba(201,206,211,0.06)' }}>
            <Heart size={22} weight={isSaved ? 'fill' : 'regular'} style={{ color: isSaved ? '#ef4444' : 'var(--text-muted)' }} />
          </button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          {gc.owner_name && <Detail icon={<User size={14} />} label="Owner" value={gc.owner_name} />}
          {gc.primary_contact && <Detail icon={<User size={14} />} label="Primary Contact" value={gc.primary_contact} />}
          {gc.email && <Detail icon={<EnvelopeSimple size={14} />} label="Email" value={gc.email} link={`mailto:${gc.email}`} />}
          {gc.phone && <Detail icon={<Phone size={14} />} label="Phone" value={gc.phone} link={`tel:${gc.phone}`} />}
          {gc.website && <Detail icon={<Globe size={14} />} label="Website" value={gc.website.replace(/^https?:\/\//, '')} link={gc.website} external />}
          {gc.bbb_number && <Detail icon={<ShieldCheck size={14} />} label="BBB Number" value={gc.bbb_number} />}
        </div>
        <div className="px-6 pb-6"><Button data-testid="gc-profile-close" onClick={onClose} className="w-full btn-ghost text-sm" style={{ border: '1px solid var(--border-secondary)' }}>Close</Button></div>
      </div>
    </div>
  );
};

const Detail = ({ icon, label, value, link, external }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5" style={{ color: 'var(--gold)' }}>{icon}</span>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</div>
      {link ? <a href={link} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} className="text-sm font-medium break-all" style={{ color: link.startsWith('mailto') || link.startsWith('tel') ? 'var(--text-primary)' : 'var(--gold)' }}>{value}</a>
        : <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{value}</div>}
    </div>
  </div>
);

const ContractorDirectory = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState('');
  const [radius, setRadius] = useState(100);
  const [selectedGC, setSelectedGC] = useState(null);
  const [searched, setSearched] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [folders, setFolders] = useState([]);
  const [activeView, setActiveView] = useState('all'); // 'all', 'saved', or folder id
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [dragOverFolder, setDragOverFolder] = useState(null);

  useEffect(() => { fetchContractors('', 500); fetchSaved(); fetchFolders(); }, []);

  const fetchContractors = async (loc, rad) => {
    setLoading(true);
    try {
      const params = {};
      if (loc) params.location = loc;
      params.radius = rad;
      const res = await axios.get(`${API}/search/contractors`, { params });
      setContractors(res.data);
      setSearched(true);
      if (loc && res.data.length === 0) toast.info('No contractors found in this area');
    } catch { toast.error('Failed to load contractors'); }
    finally { setLoading(false); }
  };

  const fetchSaved = async () => {
    try { const res = await axios.get(`${API}/saved-contractors`); setSavedIds(new Set(res.data)); } catch {}
  };

  const fetchFolders = async () => {
    try { const res = await axios.get(`${API}/gc-folders`); setFolders(res.data); } catch {}
  };

  const toggleSave = async (gcId) => {
    const wasSaved = savedIds.has(gcId);
    setSavedIds(prev => { const next = new Set(prev); if (wasSaved) next.delete(gcId); else next.add(gcId); return next; });
    try {
      if (wasSaved) { await axios.delete(`${API}/saved-contractors/${gcId}`); toast.success('Removed from saved'); fetchFolders(); }
      else { await axios.post(`${API}/saved-contractors/${gcId}`); toast.success('Contractor saved!'); }
    } catch { setSavedIds(prev => { const next = new Set(prev); if (wasSaved) next.add(gcId); else next.delete(gcId); return next; }); toast.error('Failed'); }
  };

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/gc-folders`, { name: newFolderName.trim() });
      setNewFolderName(''); setShowAddFolder(false); fetchFolders();
      toast.success('Folder created');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteFolder = async (folderId) => {
    try { await axios.delete(`${API}/gc-folders/${folderId}`); fetchFolders(); if (activeView === folderId) setActiveView('all'); toast.success('Folder deleted'); } catch { toast.error('Failed'); }
  };

  const handleDropOnFolder = async (e, folderId) => {
    e.preventDefault(); e.stopPropagation(); setDragOverFolder(null);
    const gcId = e.dataTransfer.getData('gc-id');
    if (!gcId) return;
    try {
      await axios.post(`${API}/gc-folders/${folderId}/add/${gcId}`);
      toast.success('Added to folder');
      fetchFolders(); fetchSaved();
    } catch { toast.error('Failed'); }
  };

  const handleDropOnSaved = async (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOverFolder(null);
    const gcId = e.dataTransfer.getData('gc-id');
    if (!gcId || savedIds.has(gcId)) return;
    try { await axios.post(`${API}/saved-contractors/${gcId}`); toast.success('Contractor saved!'); fetchSaved(); } catch { toast.error('Failed'); }
  };

  const handleRemoveFromFolder = async (folderId, gcId) => {
    try { await axios.delete(`${API}/gc-folders/${folderId}/remove/${gcId}`); fetchFolders(); toast.success('Removed from folder'); } catch { toast.error('Failed'); }
  };

  const handleSearch = (e) => { e.preventDefault(); fetchContractors(location, radius); };

  // Filter contractors
  const getDisplayed = () => {
    if (activeView === 'all') return contractors;
    if (activeView === 'saved') return contractors.filter(gc => savedIds.has(gc.id));
    const folder = folders.find(f => f.id === activeView);
    if (folder) return contractors.filter(gc => folder.gc_ids?.includes(gc.id));
    return contractors;
  };
  const displayedContractors = getDisplayed();
  const activeFolder = folders.find(f => f.id === activeView);

  return (
    <div className="hb-bg min-h-screen">
      <header className="hb-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-dashboard" onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="gap-2 btn-ghost"><ArrowLeft size={20} /> Back</Button>
            <h1 className="text-lg font-bold font-heading" style={{ color: 'var(--gold-light)' }}>Contractor Directory</h1>
          </div>
        </div>
      </header>

      <div className="flex max-w-7xl mx-auto" style={{ minHeight: 'calc(100vh - 73px)' }}>
        {/* ── Sidebar ── */}
        <aside className="w-56 shrink-0 p-5" style={{ background: 'rgba(12,12,18,0.7)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
          {/* All */}
          <div data-testid="gc-filter-all" onClick={() => setActiveView('all')}
            className="flex items-center gap-3 px-3 py-3 rounded-xl mb-1.5 cursor-pointer transition-all"
            style={{ background: activeView === 'all' ? 'rgba(201,206,211,0.1)' : 'transparent', color: activeView === 'all' ? 'var(--gold)' : 'var(--text-primary)' }}>
            <HardHat size={20} weight={activeView === 'all' ? 'fill' : 'duotone'} />
            <div className="flex-1"><div className="font-semibold text-sm">All</div><div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{contractors.length}</div></div>
          </div>

          {/* Saved */}
          <div data-testid="gc-filter-saved" onClick={() => setActiveView('saved')}
            onDragEnter={(e) => { e.preventDefault(); setDragOverFolder('saved'); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDragLeave={() => setDragOverFolder(null)}
            onDrop={handleDropOnSaved}
            className="flex items-center gap-3 px-3 py-3 rounded-xl mb-1.5 cursor-pointer transition-all"
            style={{ background: dragOverFolder === 'saved' ? 'rgba(239,68,68,0.15)' : activeView === 'saved' ? 'rgba(239,68,68,0.1)' : 'transparent',
              color: activeView === 'saved' ? '#ef4444' : 'var(--text-primary)',
              border: dragOverFolder === 'saved' ? '1px dashed #ef4444' : '1px solid transparent' }}>
            <Heart size={20} weight={activeView === 'saved' || savedIds.size > 0 ? 'fill' : 'duotone'} style={{ color: activeView === 'saved' || savedIds.size > 0 ? '#ef4444' : undefined }} />
            <div className="flex-1"><div className="font-semibold text-sm">Saved</div><div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{savedIds.size}</div></div>
          </div>

          {/* Custom Folders */}
          <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between px-3 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>My Folders</span>
              <button data-testid="add-gc-folder-btn" onClick={() => setShowAddFolder(!showAddFolder)} className="p-1 rounded transition-all" style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--gold)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}>
                <Plus size={14} weight="bold" />
              </button>
            </div>

            {showAddFolder && (
              <div className="px-3 mb-3">
                <input data-testid="new-gc-folder-input" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddFolder(); }}
                  placeholder="Folder name..." className="hb-input w-full text-xs px-3 py-2 mb-2 rounded-lg" autoFocus />
                <div className="flex gap-2">
                  <Button data-testid="save-gc-folder-btn" onClick={handleAddFolder} disabled={!newFolderName.trim()} size="sm" className="btn-gold text-xs flex-1">Add</Button>
                  <Button onClick={() => { setShowAddFolder(false); setNewFolderName(''); }} size="sm" className="btn-ghost text-xs flex-1" style={{ border: '1px solid var(--border-secondary)' }}>Cancel</Button>
                </div>
              </div>
            )}

            {folders.map(folder => (
              <div key={folder.id} data-testid={`gc-folder-${folder.id.slice(0,8)}`}
                onClick={() => setActiveView(folder.id)}
                onDragEnter={(e) => { e.preventDefault(); setDragOverFolder(folder.id); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragLeave={() => setDragOverFolder(null)}
                onDrop={(e) => handleDropOnFolder(e, folder.id)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl mb-1.5 cursor-pointer transition-all group"
                style={{
                  background: dragOverFolder === folder.id ? 'rgba(201,206,211,0.2)' : activeView === folder.id ? 'rgba(201,206,211,0.1)' : 'transparent',
                  color: activeView === folder.id ? 'var(--gold)' : 'var(--text-primary)',
                  border: dragOverFolder === folder.id ? '1px dashed var(--gold)' : '1px solid transparent',
                }}>
                <FolderOpen size={20} weight={activeView === folder.id ? 'fill' : 'duotone'} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{folder.name}</div>
                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{folder.gc_ids?.length || 0}</div>
                </div>
                <button data-testid={`delete-gc-folder-${folder.id.slice(0,8)}`}
                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                  className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: 'var(--text-muted)' }}>
                  <Trash size={12} />
                </button>
              </div>
            ))}

            {folders.length === 0 && !showAddFolder && (
              <p className="px-3 text-[10px]" style={{ color: 'var(--text-muted)' }}>Create folders to organize contractors</p>
            )}
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 p-6">
          {/* Search */}
          <form onSubmit={handleSearch} className="mb-6" data-testid="contractor-search-form">
            <div className="hb-card p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <MapPin size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--gold)' }} />
                  <Input data-testid="contractor-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Filter by city, address, or ZIP..." className="hb-input pl-9" />
                </div>
                <div className="flex items-center gap-2">
                  <select data-testid="contractor-radius-select" value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="hb-input h-10 px-3 rounded-lg text-sm" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}>
                    {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} miles</option>)}
                  </select>
                  <Button type="submit" data-testid="contractor-search-btn" disabled={loading} className="btn-gold gap-2 h-10 px-6">
                    <MagnifyingGlass size={16} weight="bold" /> {loading ? 'Searching...' : 'Search'}
                  </Button>
                </div>
              </div>
              {location && searched && <button type="button" onClick={() => { setLocation(''); fetchContractors('', 500); }} className="text-xs mt-2 font-semibold" style={{ color: 'var(--gold)' }}>Clear filter</button>}
            </div>
          </form>

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {activeView === 'all' ? `${displayedContractors.length} contractors` :
               activeView === 'saved' ? `${displayedContractors.length} saved` :
               activeFolder ? `${displayedContractors.length} in "${activeFolder.name}"` : ''}
              {location ? ` within ${radius} mi of "${location}"` : ''}
            </p>
            {activeFolder && (
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Drag contractors here to add</p>
            )}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="text-center py-16"><img src="/harperbids-logo.png" alt="Loading" className="h-12 opacity-50 animate-pulse mx-auto hb-logo-glow" /></div>
          ) : displayedContractors.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="contractor-grid">
              {displayedContractors.map(gc => (
                <ContractorCard key={gc.id} gc={gc} onExpand={setSelectedGC} isSaved={savedIds.has(gc.id)} onToggleSave={toggleSave} />
              ))}
            </div>
          ) : (
            <div className="hb-empty p-16 text-center">
              {activeView === 'saved' ? (
                <><Heart size={64} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <h3 className="text-xl font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>No saved contractors</h3>
                <p style={{ color: 'var(--text-secondary)' }}>Click the heart on any contractor card to save them.</p></>
              ) : activeFolder ? (
                <><FolderOpen size={64} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <h3 className="text-xl font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>"{activeFolder.name}" is empty</h3>
                <p style={{ color: 'var(--text-secondary)' }}>Drag contractor cards into this folder from the All view.</p></>
              ) : (
                <><HardHat size={64} weight="duotone" className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <h3 className="text-xl font-semibold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>No contractors found</h3>
                <p style={{ color: 'var(--text-secondary)' }}>Try a wider radius or different location.</p></>
              )}
            </div>
          )}
        </main>
      </div>

      {selectedGC && <ProfileModal gc={selectedGC} onClose={() => setSelectedGC(null)} isSaved={savedIds.has(selectedGC.id)} onToggleSave={toggleSave} />}
    </div>
  );
};

export default ContractorDirectory;
